import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Ledger from './pages/Ledger';
import CashPool from './pages/CashPool';
import Cycles from './pages/Cycles';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import AdminApprovals from './pages/AdminApprovals';
import CompanyPnL from './pages/CompanyPnL';
import Users from './pages/Users';
import AuditLogs from './pages/AuditLogs';
import ClientReportV3 from './pages/ClientReportV3';
import DataQuality from './pages/DataQuality';
import Settings from './pages/Settings';
import Layout from './components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API
});

axiosInstance.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Auto-attach Idempotency-Key to every POST so backend can dedupe network retries
  // (load-balancer retry, axios retry, browser retry). Same axios call retried at
  // network level reuses these headers, hence same key. New click → new key.
  if (config.method && config.method.toLowerCase() === 'post' && !config.headers['Idempotency-Key']) {
    const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    config.headers['Idempotency-Key'] = uuid;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
        <Route
          path="/client-report-v3/:clientId"
          element={user ? <ClientReportV3 /> : <Navigate to="/login" />}
        />
        <Route
          path="/*"
          element={
            user ? (
              <Layout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/clients/:clientId/report-v3" element={<ClientReportV3 />} />
                  <Route path="/ledger" element={<Ledger />} />
                  <Route path="/cash-pool" element={<CashPool />} />
                  <Route path="/cycles" element={<Cycles />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/company-pnl" element={<CompanyPnL />} />
                  {user.is_admin && <Route path="/approvals" element={<AdminApprovals />} />}
                  {user.is_admin && <Route path="/users" element={<Users />} />}
                  {user.is_admin && <Route path="/audit-logs" element={<AuditLogs />} />}
                  {user.is_admin && <Route path="/data-quality" element={<DataQuality />} />}
                  {user.is_admin && <Route path="/settings" element={<Settings />} />}
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
